import { useLocation, useParams } from 'react-router-dom';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { ExtensionFindingType } from '@kirin/api-client/contracts/analyses';
import {
  formatAlertType,
  formatSubPolicyLabel,
  isHiddenCharacterAlertType,
  type AlertDescriptionSections,
} from '@kirin/api-client/helpers/alerts';
import {
  formatAlertDescriptionSections,
  formatAlertDescriptionText,
} from '@kirin/api-client/helpers/alert-risk-text';
import { formatSubPolicyMitigation } from '@kirin/api-client/data/alert-mitigations';
import {
  MULTI_TOOL_PLATFORM_ID,
  toPlatformId,
} from '@kirin/api-client/contracts/platform-registry';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import type { OsvComponent } from '@kirin/api-client/contracts/shared/osv';
import {
  SECRET_DETECTION_GITLEAKS_RULES_SLUG,
  policySubPath,
} from '@/lib/policyPaths';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import type { AlertSecretDetailsData } from './AlertSecretDetails.helpers';
import {
  getMcpLocationEvidence,
  trimHiddenMessageLocationSuffix,
  type McpLocationEvidence,
} from './alert-location';
import {
  EXTENSION_SCAN_RESULT_FROM_ALERTS,
  buildExtensionScanResultLink,
} from './extension-scan-result-link';
import {
  getConfigJsonBlock,
  getOsvAdvisoryId,
  getOsvComponent,
} from './alert-detail-cards.helpers';
import { parseJoinedFactors } from './DetectionLogic/resolve';

/**
 * The alert fields the detail model derives from — the exact slice both alert
 * detail layouts consume. `securityGroups` is optional: absent when its flag is
 * off and on the personal dashboard, which is also the right "no group" answer.
 */
export type AlertDetailModelInput = Pick<
  GroupedAlert,
  | 'type'
  | 'severity'
  | 'description'
  | 'location'
  | 'filePath'
  | 'subPolicyKey'
  | 'sourceDetails'
  | 'scoreBreakdown'
  | 'outcome'
  | 'engine'
  | 'ruleId'
  | 'ruleName'
  | 'entropy'
  | 'toolName'
  | 'sessionId'
  | 'workspaceName'
  | 'matchContext'
  // KIR-4212: chat-security evidence excerpt (representative on grouped alerts).
  | 'chatExcerpt'
  // KIR-4213: command-file evidence excerpt (representative on grouped alerts).
  | 'commandExcerpt'
  | 'toolCallScan'
  | 'project'
  | 'clientTypes'
  // KIR-4367: latest occurrence's client. Absent on older payloads.
  | 'clientType'
  // KIR-4270: risk-assessment text inputs (tool-event side, chat pattern,
  // group size for the gateway occurrence rule).
  | 'eventKind'
  | 'detectionPattern'
  | 'occurrenceCount'
> &
  Partial<Pick<GroupedAlert, 'securityGroups'>>;

/** Everything both alert-detail layouts derive from an alert + the flag state. */
export interface AlertDetailModel {
  organizationId: string | undefined;
  revampEnabled: boolean;
  sourceDetailsEnabled: boolean;
  capabilityDetectionEnabled: boolean;
  secretDetailsEnabled: boolean;
  detectionLogicEnabled: boolean;
  gitleaksRulesPath: string | undefined;
  /** Already gated on `sourceDetailsEnabled` (undefined when the flag is off). */
  osvComponent: OsvComponent | undefined;
  osvAdvisoryId: string | undefined;
  configJsonBlock: string | undefined;
  isHiddenCharacterAlert: boolean;
  secretDetails: AlertSecretDetailsData;
  label: string;
  description: string;
  descriptionSections: AlertDescriptionSections | null;
  cardSections: AlertDescriptionSections | null;
  /**
   * KIR-4271: the Security-approved "Recommended actions" copy for this
   * mapping. Already gated (null when `alert_recommended_actions` or
   * `alerts_revamp` is off, and when the mapping has no approved copy), so a
   * layout renders the section on `mitigation != null` alone.
   */
  mitigation: string | null;
  surroundingText: string | undefined;
  isExtensionSecurityAlert: boolean;
  mcpLocation: McpLocationEvidence | null;
  locationForDisplay: string | null;
  scanResultLink: string | null;
  /**
   * KIR-4212: the masked chat-security evidence excerpt, only for
   * `ai-detected-threat` and only when present. Ungated (already secret- and
   * PII-screened at ingest) — both layouts render it whenever it exists.
   */
  chatExcerpt: string | undefined;
  /**
   * KIR-4213: the masked command-file evidence excerpt, only for
   * `command-file-threat` and only when present. Ungated (already secret- and
   * PII-screened at ingest) — both layouts render it whenever it exists.
   */
  commandExcerpt: string | undefined;
  /**
   * PII-screened excerpt of the redacted tool_input for a `tool-call-scan`
   * finding (Bash command when the payload is JSON). Ungated — already
   * secret- and PII-screened at ingest.
   */
  toolCallExcerpt: string | undefined;
  /**
   * KIR-4212: for a chat-security alert the Location is the classifier's
   * `key_factors` re-joined, which Detection Logic already renders as "Key
   * factors". Drop the duplicate Location, but only when Detection Logic is on to
   * render those factors — with it off, Location is their only home. NOT applied
   * to `command-file-threat`: its Location is a navigable file path, not
   * duplicated factors (KIR-4213).
   */
  suppressRedundantLocation: boolean;
  /**
   * KIR-5046: the Project a `multi_tool` ("Multi-IDE / Platform") alert came
   * from, shown as a row in "Detected on client". Kept apart from
   * `secretDetails.project` so these alerts don't open the secret panel. Set
   * only when the alert carries a project (the Alerts Project column's gate).
   */
  clientProject: GroupedAlert['project'];
}

/**
 * KIR-4481 — the single source of the per-alert derivation shared by the legacy
 * {@link AlertDetailCard} and the redesigned `AlertDetailCards`. Both layouts
 * read the same flags and compute the same label / description / sections /
 * source-detail / location values; keeping that logic here stops the two copies
 * from drifting during the `alerts_grouped_cards` rollout. Presentation-only —
 * no data is fetched or mutated here.
 *
 * @param alert - the alert fields the model derives from.
 * @param organizationIdProp - org scope for scan-result links; falls back to the
 *   route `organizationId` param when omitted.
 * @param variant - `'occurrence'` when the alert is one occurrence of a group.
 * @returns the derived values plus the resolved feature-flag booleans.
 */
export function useAlertDetailModel(
  alert: AlertDetailModelInput,
  organizationIdProp?: string,
  variant: 'group' | 'occurrence' = 'group'
): AlertDetailModel {
  const routerLocation = useLocation();
  const { organizationId: organizationIdFromRoute } = useParams<{
    organizationId?: string;
  }>();
  const organizationId = organizationIdProp ?? organizationIdFromRoute;

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.ALERT_SOURCE_DETAILS,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.SECRET_DETECTION_DETAILS,
    flagsmithFeatureFlags.ALERT_DETECTION_LOGIC,
    flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT,
    flagsmithFeatureFlags.ALERT_RECOMMENDED_ACTIONS,
    flagsmithFeatureFlags.ALERT_RISK_ASSESSMENT_TEXT,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  const sourceDetailsEnabled =
    flags[flagsmithFeatureFlags.ALERT_SOURCE_DETAILS] === true;
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const secretDetailsEnabled =
    flags[flagsmithFeatureFlags.SECRET_DETECTION_DETAILS] === true;
  // KIR-4272: Detection Logic replaces the extension-only "Why this fired"
  // section and applies to every alert type, so it has its own flag.
  const detectionLogicEnabled =
    flags[flagsmithFeatureFlags.ALERT_DETECTION_LOGIC] === true;
  // KIR-4590: passing the path rather than a boolean keeps the resolver pure.
  const gitleaksRulesPath =
    flags[flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT] === true
      ? policySubPath(organizationId, SECRET_DETECTION_GITLEAKS_RULES_SLUG)
      : undefined;

  // KIR-4190: OSV fields live on the extension and mcp variants only; gated on
  // `alert_source_details` like the rest of the source detail.
  const osvComponent = sourceDetailsEnabled
    ? getOsvComponent(alert.sourceDetails)
    : undefined;
  const osvAdvisoryId = getOsvAdvisoryId(alert.sourceDetails);
  const configJsonBlock = getConfigJsonBlock(alert.sourceDetails);

  // KIR-4229: `matchContext` is shared with the hidden-character excerpt, but
  // this panel labels it "around the secret" and `hasSecretDetails` treats it as
  // enough to open "More details" — so a hidden-character alert would grow a
  // secret panel whose only row mislabels the excerpt.
  const isHiddenCharacterAlert = isHiddenCharacterAlertType(alert.type);
  const secretDetails: AlertSecretDetailsData = {
    engine: alert.engine,
    ruleId: alert.ruleId,
    ruleName: alert.ruleName,
    entropy: alert.entropy,
    toolName: alert.toolName,
    sessionId: alert.sessionId,
    workspaceName: alert.workspaceName,
    matchContext: isHiddenCharacterAlert ? undefined : alert.matchContext,
    // KIR-5075: non-secret alerts can carry `project` too (Alerts Project
    // column). Only a secret's project belongs in this secret panel, or any
    // alert with a project would open "More details".
    project: alert.type === 'secret-leakage' ? alert.project : undefined,
    // KIR-4367: the transcript path belongs to the latest occurrence. A
    // present clientType wins, including null (that row had no client, so
    // do not name an older IDE). When the field is absent — a payload from
    // before this field existed — a single-IDE group still shows that IDE.
    // An unknown clientType does not fall through.
    platform:
      alert.clientType !== undefined
        ? toPlatformId(alert.clientType)
        : alert.clientTypes?.length === 1
          ? toPlatformId(alert.clientTypes[0])
          : undefined,
  };

  const label = revampEnabled
    ? formatSubPolicyLabel(alert.subPolicyKey, alert.type)
    : formatAlertType(alert.type);
  // KIR-4270: Security's risk wording, curated copy only (inside the revamp).
  // The composer also keeps the KIR-3287 gateway-surface then registry order.
  const descriptionOptions = {
    riskAssessmentText:
      flags[flagsmithFeatureFlags.ALERT_RISK_ASSESSMENT_TEXT] === true,
  };
  // An occurrence is one event; its projection omits the group's count.
  const riskInput =
    variant === 'occurrence' ? { ...alert, occurrenceCount: 1 } : alert;
  const description = revampEnabled
    ? formatAlertDescriptionText(riskInput, descriptionOptions)
    : alert.description;
  const descriptionSections = revampEnabled
    ? formatAlertDescriptionSections(riskInput, descriptionOptions)
    : null;
  // KIR-4272: Detection Logic owns every per-finding signal, so the `detected`
  // sub-block would render the same string twice on one card. Dropped only when
  // the flag is ON — with it off the legacy surfaces stay byte-identical.
  const cardSections =
    detectionLogicEnabled && descriptionSections != null
      ? {
          whatHappened: descriptionSections.whatHappened,
          whyItMatters: descriptionSections.whyItMatters,
        }
      : descriptionSections;

  // KIR-4271: gated on `revampEnabled` as well as its own flag. The mitigation
  // is the fifth column of the same curated sheet `alerts_revamp` gates, so it
  // must render beside the curated title and body, never over a raw scanner
  // description. Resolved by its OWN lookup, not off `descriptionSections`:
  // gateway surfaces and weighted extension bodies replace those sections, and
  // a mitigation derived from them would vanish for exactly those findings.
  //
  // THIS GATE IS WIDER THAN v1'S, ON PURPOSE. `toPublicAlert` checks
  // `alert_recommended_actions` alone, because `alerts_revamp` is a decision
  // about what this panel renders and the API has no raw-copy mode to protect.
  // The visible consequence: an org with recommended actions on and the revamp
  // off gets the guidance over `/api/v1` while this screen shows none. That is
  // the intended reading of the two flags, not drift — change one side and you
  // owe the other an edit.
  //
  // WHEN `alerts_revamp` IS RETIRED, drop the `revampEnabled &&` conjunct and
  // KEEP the flag check: `alert_recommended_actions` outlives the rollout and
  // stays its own gate. The cleanup that retires a rollout flag unwraps every
  // `revampEnabled ? … : …` and deletes every `revampEnabled &&`, and treating
  // this line as one more of those would collapse the condition to `true` and
  // silently widen the section to every org with the revamp on.
  const mitigation =
    revampEnabled &&
    flags[flagsmithFeatureFlags.ALERT_RECOMMENDED_ACTIONS] === true
      ? formatSubPolicyMitigation(alert.subPolicyKey, alert.type)
      : null;

  // KIR-4229: the surrounding-text excerpt for an invisible-unicode finding.
  // Only when Detection Logic is off (it renders the excerpt itself); the type
  // guard keeps the secret-leakage `matchContext` out of this section.
  const surroundingText =
    !detectionLogicEnabled && isHiddenCharacterAlert
      ? alert.matchContext
      : undefined;

  const isExtensionSecurityAlert =
    alert.sourceDetails?.kind === 'extension' ||
    alert.scoreBreakdown !== undefined ||
    alert.type
      .toLowerCase()
      .includes(ExtensionFindingType.ExcessivePermissions);

  // KIR-4175: for an MCP tool-description alert, render the Location as the
  // tool/server identity + the description WINDOW around the match instead of
  // the head-truncated preview. Display-only; the stored `location` is
  // untouched, so the finding_key is unaffected. `null` for every other type.
  const mcpLocation = getMcpLocationEvidence(
    alert.sourceDetails,
    alert.location
  );
  // KIR-4229: Detection Logic's "Decoded message" row and the Location's
  // `Message: "…"` line carry the same payload; drop it from the Location where
  // that row renders it (steganography only). Display-only.
  const locationForDisplay =
    // KIR-4213 product review (Kobi): a command-file alert's Location is a file
    // path — show the same label as the File column (original-case `filePath`,
    // falling back to `location`) instead of the anonymized/canonical form on
    // `location`. Reuse `getAlertFileDisplayLabel` so it matches the File column
    // by construction and an empty-string `filePath` (a real `mergePhaseRows`
    // value) can't win over a valid `location`.
    alert.type === 'command-file-threat'
      ? getAlertFileDisplayLabel({
          filePath: alert.filePath,
          location: alert.location,
        })
      : detectionLogicEnabled && alert.type === 'steganography'
        ? trimHiddenMessageLocationSuffix(alert.location)
        : alert.location;

  const scanResultLink =
    capabilityDetectionEnabled && isExtensionSecurityAlert
      ? buildExtensionScanResultLink({
          location: alert.location,
          sourceDetails: alert.sourceDetails,
          organizationId,
          from: EXTENSION_SCAN_RESULT_FROM_ALERTS,
          returnTo: `${routerLocation.pathname}${routerLocation.search}`,
        })
      : null;

  // KIR-4212: chat-security evidence excerpt + the resulting Location redundancy.
  const chatExcerpt =
    alert.type === 'ai-detected-threat' ? alert.chatExcerpt : undefined;
  // KIR-4213: command-file evidence excerpt (same shape as the chat excerpt).
  const commandExcerpt =
    alert.type === 'command-file-threat' ? alert.commandExcerpt : undefined;
  const toolCallExcerpt =
    alert.type === 'tool-call-scan' ? alert.toolCallScan?.excerpt : undefined;
  // Only suppress the Location when Detection Logic actually renders the key
  // factors it would duplicate. `parseJoinedFactors` returns [] for a labelled
  // location (`Command:`/`Text:`/`File:`…), and an LLM `key_factors` entry can
  // start with one — hiding the row would then drop the factors from BOTH places
  // (Imri/Gidi M4). Scoped to `ai-detected-threat` only: that type's Location IS
  // the comma-joined key_factors. `command-file-threat` (like
  // `ai-instructions-threat`) instead carries a navigable file path plus a
  // `Why flagged:` block on Location, so suppressing it would hide the path —
  // the Command excerpt block shows the factors without dropping the path.
  const suppressRedundantLocation =
    detectionLogicEnabled &&
    alert.type === 'ai-detected-threat' &&
    parseJoinedFactors(alert.location).length > 0;

  const isMultiToolAlert =
    alert.clientType === MULTI_TOOL_PLATFORM_ID ||
    (alert.clientTypes ?? []).includes(MULTI_TOOL_PLATFORM_ID);
  const clientProject =
    alert.type !== 'secret-leakage' && isMultiToolAlert
      ? alert.project
      : undefined;

  return {
    organizationId,
    revampEnabled,
    sourceDetailsEnabled,
    capabilityDetectionEnabled,
    secretDetailsEnabled,
    detectionLogicEnabled,
    gitleaksRulesPath,
    osvComponent,
    osvAdvisoryId,
    configJsonBlock,
    isHiddenCharacterAlert,
    secretDetails,
    label,
    description,
    descriptionSections,
    cardSections,
    mitigation,
    surroundingText,
    isExtensionSecurityAlert,
    mcpLocation,
    locationForDisplay,
    scanResultLink,
    chatExcerpt,
    commandExcerpt,
    toolCallExcerpt,
    suppressRedundantLocation,
    clientProject,
  };
}
